import React from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Container, Typography, useMediaQuery, useTheme, Accordion, AccordionSummary, AccordionDetails, Grid, Skeleton, Stack } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import HeroSection from "../../components/common/HeroSection";
import { getVisaFilePage } from "../../services/VisaAppointmentPageService";
import { HeadingText } from "../../utils/CustomTagComponents";

const BookVisaAppointment = () => {
  const { country_id, country_name } = useSelector((state) => state.auth || {});

  const { data: programData, isLoading: loading } = useQuery({
    queryKey: ["visaAppointmentPage", country_id],
    queryFn: async () => {
      if (!country_id) return null;
      const res = await getVisaFilePage(country_id);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      }
      return null;
    },
    enabled: !!country_id,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getResponsiveHtml = (html) => {
    if (!html) return "";
    let clean = html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/gi, " ")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/\[empty_line\]/gi, "<br />");
    if (!isMobile) return clean;
    return clean.replace(/font-weight:\s*900/gi, "font-weight: 700");
  };

  // if (loading)
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
  //       <CircularProgress color="warning" />
  //     </Box>
  //   );

  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Grid container spacing={2} alignItems="center">

          {/* Left skeleton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="text" height={60} width="80%" />
            <Skeleton variant="text" height={30} width="90%" />
            <Skeleton variant="text" height={30} width="85%" />

            <Stack direction="row" spacing={2} mt={3}>
              <Skeleton variant="rounded" width={140} height={45} />
              <Skeleton variant="rounded" width={140} height={45} />
            </Stack>

            <Stack spacing={2} mt={3}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="75%" />
            </Stack>
          </Grid>

          {/* Right skeleton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton
              variant="rectangular"
              height={420}
              sx={{ borderRadius: 2 }}
            />
          </Grid>

        </Grid>
      </Container>
    );
  }

  if (!programData)
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <p>No visa file data found for {country_name || "selected country"}.</p>
      </Box>
    );

  const heroTitle = programData.hero_section_title || "";
  const heroSubtitle = programData.hero_section_desc || "";
  const heroImage = programData.hero_section_image || "";
  const heroImageType = programData.hero_section_image_type || "image";
  const about_title = programData.about_title || "";
  const about_desc = programData.about_desc || "";
  const doc_title = programData.doc_title || "";
  const docs_list = programData.docs_list || [];
  const mid = Math.ceil(docs_list.length / 2);
  const leftColumn = docs_list.slice(0, mid);
  const rightColumn = docs_list.slice(mid);
  const application_title = programData.application_title || "";
  const application_desc = programData.application_desc || "";

  const contentStyles = {
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.625,
    textAlign: "justify",
    "& p": { marginBlockStart: '0px !important', marginBottom: "16px" },
    "& p:last-of-type": { marginBottom: "0px" },
    "& ul": { listStyle: "none", paddingLeft: "0px", marginBottom: "12px" },
    "& li": { position: "relative", paddingLeft: "28px", marginBottom: "4px", lineHeight: 1.7 },
    "& li:first-of-type": { marginTop: "-10px" },
    "& li:last-of-type": { marginBottom: "15px" },
    "& li::before": { content: '"\\f35a"', fontFamily: "FontAwesome", position: "absolute", left: '5px', top: "2px", fontSize: "14px", color: "#fd0000" },
    "& h1, & h2, & h3, & h4": { marginTop: "20px", marginBottom: "12px", fontWeight: 600 },
    "& strong": { fontWeight: 600 },
  };

  return (
    <>
      <HeroSection
        titleContent={heroTitle}
        subtitleContent={heroSubtitle}
        heroImage={heroImage}
        heroImageType={heroImageType}
      />
      {about_title && about_desc && (
        <Container sx={{ py: { xs: 2, sm: 2, md: 3 }, px: { xs: 3, sm: 4, md: 6 }, paddingLeft: { xs: "24px !important", md: "48px !important" }, paddingRight: { xs: "24px !important", md: "48px !important" } }}>
          <Box sx={{ display: "inline-block", position: "relative", mb: 2, "&::after": { content: '""', position: "absolute", left: 0, bottom: -8, width: "200px", height: "2px", backgroundColor: "#fd0000", opacity: "0.5" } }}>
            <HeadingText variant="h4" sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" } }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(about_title) }} />
          </Box>
          <Box sx={{ borderRadius: "10px", width: "100%", zIndex: 2, display: "flex", mt: 1 }}>
            <Typography sx={{ ...contentStyles, fontSize: { xs: "14px", sm: "16px" }, textAlign: "justify" }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(about_desc) }} />
          </Box>
        </Container>
      )}

      {doc_title && (
        <Container sx={{ py: { xs: 2, sm: 2, md: 3 }, px: { xs: 3, sm: 4, md: 6 }, paddingLeft: { xs: "24px !important", md: "48px !important" }, paddingRight: { xs: "24px !important", md: "48px !important" } }}>
          <Box sx={{ display: "inline-block", position: "relative", mb: 2, "&::after": { content: '""', position: "absolute", left: 0, bottom: -8, width: "200px", height: "2px", backgroundColor: "#fd0000", opacity: "0.5" } }}>
            <HeadingText sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" }, fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(doc_title) }} />
          </Box>
          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 2, rowGap: 1, alignItems: "start" }}>
            <Box>
              {leftColumn.map((item, index) => (
                <Accordion key={index} disableGutters sx={{ borderRadius: "10px !important", mb: 1.5, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb", "&:before": { display: "none" }, transition: "0.2s", "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.08)" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: "50px !important", px: 2, "& .MuiAccordionSummary-content": { margin: "8px 0" } }}>
                    <Typography sx={{ fontSize: { xs: "14px", sm: "16px" }, fontWeight: 500 }}>{index + 1}. {item.key}</Typography>
                  </AccordionSummary>
                  {item.value && <AccordionDetails sx={{ background: "#f9fafb", borderTop: "1px solid #eee", px: 2, py: 1.5 }}>
                    <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#374151", lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: item.value }} />
                  </AccordionDetails>}
                </Accordion>
              ))}
            </Box>
            <Box>
              {rightColumn.map((item, index) => (
                <Accordion key={index + mid} disableGutters sx={{ borderRadius: "10px !important", mb: 1.5, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb", "&:before": { display: "none" }, transition: "0.2s", "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.08)" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: "50px !important", px: 2, "& .MuiAccordionSummary-content": { margin: "8px 0" } }}>
                    <Typography sx={{ fontSize: { xs: "14px", sm: "16px" }, fontWeight: 500 }}>{index + mid + 1}. {item.key}</Typography>
                  </AccordionSummary>
                  {item.value && <AccordionDetails sx={{ background: "#f9fafb", borderTop: "1px solid #eee", px: 2, py: 1.5 }}>
                    <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#374151", lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: item.value }} />
                  </AccordionDetails>}
                </Accordion>
              ))}
            </Box>
          </Box>
        </Container>
      )}

      {application_title && application_desc && (
        <Container sx={{ py: { xs: 2, sm: 2, md: 3 }, px: { xs: 3, sm: 4, md: 6 }, paddingLeft: { xs: "24px !important", md: "48px !important" }, paddingRight: { xs: "24px !important", md: "48px !important" } }}>
          <Box sx={{ display: "inline-block", position: "relative", mb: 2, "&::after": { content: '""', position: "absolute", left: 0, bottom: -8, width: "200px", height: "2px", backgroundColor: "#fd0000", opacity: "0.5" } }}>
            <HeadingText sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" }, fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(application_title) }} />
          </Box>
          <Box sx={{ borderRadius: "10px", mt: { xs: 2, md: 0 }, width: "100%", zIndex: 2, display: "flex" }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "16px" }, textAlign: "justify", ...contentStyles }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(application_desc) }} />
          </Box>
        </Container>
      )}
    </>
  );
};

export default BookVisaAppointment;
